import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// mass units → grams
const M = { mg:0.001, g:1, kg:1000, t:1e6, oz:28.3495, lb:453.592, ton:907185 };
const M_LABEL = { mg:'mg — milligram', g:'g — gram', kg:'kg — kilogram', t:'t — metric ton', oz:'oz — ounce', lb:'lb — pound', ton:'ton — short ton' };
// volume units → cubic meters
const V = { ml:1e-6, l:1e-3, m3:1, cm3:1e-6, in3:1.6387e-5, ft3:0.0283168, yd3:0.7645549, 'gal(US)':0.00378541 };
const V_LABEL = { ml:'mL — milliliter', l:'L — liter', m3:'m³ — cubic meter', cm3:'cm³ — cubic centimeter', in3:'in³ — cubic inch', ft3:'ft³ — cubic foot', yd3:'yd³ — cubic yard', 'gal(US)':'gal — US gallon' };

export default function DensityCalc() {
  const [mode, setMode] = useState('density'); // density | mass | volume
  const [mass, setMass] = useState(1);
  const [massUnit, setMassUnit] = useState('kg');
  const [volume, setVolume] = useState(1);
  const [volUnit, setVolUnit] = useState('l');
  const [density, setDensity] = useState(1000);

  const result = useMemo(() => {
    const kg = Number(mass) * M[massUnit] / 1000; // to kg
    const m3 = Number(volume) * V[volUnit];
    if (mode === 'density') {
      if (!m3) return null;
      return { label: 'Density (kg/m³)', value: (kg/m3).toFixed(4) };
    }
    if (mode === 'mass') {
      return { label: `Mass (${massUnit})`, value: ((Number(density) * m3) * 1000 / M[massUnit]).toFixed(4) };
    }
    if (!Number(density)) return null;
    return { label: `Volume (${volUnit})`, value: ((kg / Number(density)) / V[volUnit]).toFixed(4) };
  }, [mode, mass, massUnit, volume, volUnit, density]);

  return (
    <CalcShell look="cnet" title="Density Calculator" subtitle="Compute density, mass, or volume — ρ = m / V.">
      <div className="flex gap-2 border-b mb-4">
        {[['density','Find Density'],['mass','Find Mass'],['volume','Find Volume']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          {mode !== 'mass' && (
            <div className="field"><label>Mass</label>
              <div className="flex gap-2">
                <input type="number" value={mass} onChange={e => setMass(e.target.value)} className="flex-1" data-testid="density-mass"/>
                <select value={massUnit} onChange={e => setMassUnit(e.target.value)}>{Object.keys(M).map(u => <option key={u} value={u}>{M_LABEL[u]}</option>)}</select>
              </div>
            </div>
          )}
          {mode !== 'volume' && (
            <div className="field"><label>Volume</label>
              <div className="flex gap-2">
                <input type="number" value={volume} onChange={e => setVolume(e.target.value)} className="flex-1"/>
                <select value={volUnit} onChange={e => setVolUnit(e.target.value)}>{Object.keys(V).map(u => <option key={u} value={u}>{V_LABEL[u]}</option>)}</select>
              </div>
            </div>
          )}
          {mode !== 'density' && (
            <div className="field"><label>Density (kg/m³)</label>
              <input type="number" value={density} onChange={e => setDensity(e.target.value)}/>
            </div>
          )}
        </div>
        <div>
          {result && (
            <div className="p-5 rounded-xl bg-emerald-50 border border-emerald-200">
              <div className="text-sm text-slate-600">{result.label}</div>
              <div className="text-4xl font-bold text-emerald-700" data-testid="density-result">{result.value}</div>
            </div>
          )}
        </div>
      </div>
    </CalcShell>
  );
}
